// libs
import { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import { ListBox } from 'primereact/listbox'

import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'

// styles
import style from '@aytm/app/stats/page/sidebar/components/styles/SelectOverlay.module.scss'

// The list moves to the shared overlay so the panel can wear the sidebar's own skin; the trigger
// keeps the theme's dropdown markup, so `p-dropdown-rounded` and its siblings still dress it and
// whatever reads its label or its trigger goes on reading them.
const renderTrigger = ({ className, label, disabled, rest }) => (
  <div
    className={classNames('p-component p-dropdown', className, { 'p-disabled': disabled })}
    aria-disabled={disabled || undefined}
    {...rest}
  >
    <span className="p-dropdown-label p-inputtext">{label}</span>
    <div className="p-dropdown-trigger">
      <span className="p-dropdown-trigger-icon p-clickable pi pi-chevron-down" />
    </div>
  </div>
)

const SelectOverlay = ({
  className,
  value,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  itemTemplate,
  valueTemplate,
  filter = false,
  filterBy,
  filterPlaceholder,
  onChange,
  ...rest
}) => {
  const ref = useRef(null)

  const selected = useMemo(() => options.find(option => option.key === value) ?? null, [options, value])

  // A value template is handed the option AND the control's own props, the way the select it
  // replaces hands them over -- the empty branch of every one of ours reads the placeholder off
  // that second argument.
  const label = valueTemplate
    ? valueTemplate(selected, { placeholder, options, value, disabled })
    : selected?.text ?? placeholder

  // Clicking the selected row again clears a ListBox's value; a select has no empty state, so the
  // row is treated as a re-pick and the panel simply closes.
  const onListChange = event => {
    ref.current?.hide()

    if (null != event.value) onChange(event.value)
  }

  if (disabled) return renderTrigger({ className, label, disabled, rest })

  return (
    <OverlayPanel
      ref={ref}
      panelClassName={style.panel}
      button={renderTrigger({ className, label, disabled, rest })}
      appendTo={true}
    >
      <ListBox
        value={value}
        options={options}
        optionValue="key"
        optionLabel="text"
        optionDisabled="disabled"
        itemTemplate={itemTemplate}
        filter={filter}
        filterBy={filterBy}
        filterPlaceholder={filterPlaceholder}
        onChange={onListChange}
      />
    </OverlayPanel>
  )
}

SelectOverlay.propTypes = {
  className: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  itemTemplate: PropTypes.func,
  valueTemplate: PropTypes.func,
  filter: PropTypes.bool,
  filterBy: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  onChange: PropTypes.func
}

SelectOverlay.displayName = 'SelectOverlay'

export default SelectOverlay
